/**
 * ProjectExternalContactsSection
 * "Kontaktlista" — manages the people involved in a project, both internal team
 * members and external parties, each with an optional note describing their role.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Button, Badge, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Plus, User, Mail, Phone, Building, ChevronDown, ChevronRight, Trash2, Edit2, Info } from 'lucide-react';
import { ProjectModel, ProjectExternalContact } from '../../../types/projects';
import { ExternalUser } from '../../../types/guestAccess';
import { Userprofile } from '../../../Helpers/UsersContext';
import { AddExternalContactModal } from './AddExternalContactModal';
import { ConfirmDeleteModal } from '../../../Components/ConfirmDeleteModal';
import { MetadataInfoButton } from '../../../Components/MetadataInfoButton';
import { useUserNames } from '../../../Helpers/useUserNames';

const KONTAKTLISTA_INFO =
    'Kontaktlistan samlar alla personer som är involverade i projektet — både interna ' +
    'medarbetare och externa parter (t.ex. entreprenörer och leverantörer). Lägg till en ' +
    'anteckning för att beskriva varje persons roll i projektet.';

interface ProjectExternalContactsSectionProps {
    project: ProjectModel;
    externalUsers: ExternalUser[];
    /** Internal app users that can be picked when adding internal contacts. */
    internalUsers: Userprofile[];
    /** Resolves any UID to a display name (covers users not in internalUsers,
     *  e.g. internal contacts added on iOS without web access). */
    getUserName: (uid: string) => string;
    onAddContact: (userId: string, projectNote: string | undefined, contactType: 'internal' | 'external') => Promise<void>;
    onRemoveContact: (externalUserId: string) => Promise<void>;
    onEditNote: (externalUserId: string, note: string) => Promise<void>;
    onRefetchUsers: () => Promise<void>;
    submitting?: boolean;
    /** Admins can add/remove/edit; non-admins see a read-only list. */
    isAdmin: boolean;
}

/** Normalized contact details, resolved from either an internal or external user. */
interface ContactDisplay {
    name: string;
    isInternal: boolean;
    company?: string;
    email?: string;
    phone?: string;
    role?: string;
}

interface ContactItemProps {
    contact: ProjectExternalContact;
    display: ContactDisplay;
    onRemove: () => void;
    onEditNote: (note: string) => Promise<void>;
    submitting: boolean;
    addedByName?: string;
    isAdmin: boolean;
}

const ContactItem: React.FC<ContactItemProps> = ({
    contact,
    display,
    onRemove,
    onEditNote,
    submitting,
    addedByName,
    isAdmin,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [editingNote, setEditingNote] = useState(false);
    const [noteValue, setNoteValue] = useState(contact.projectNote || '');

    const handleSaveNote = async () => {
        await onEditNote(noteValue);
        setEditingNote(false);
    };

    const handleCancelNote = () => {
        setNoteValue(contact.projectNote || '');
        setEditingNote(false);
    };

    const hasContactDetails = display.company || display.email || display.phone;

    return (
        <div style={{ marginBottom: '8px', border: '1px solid #dee2e6', borderRadius: '10px', backgroundColor: '#fff' }}>
            <div
                style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                }}
                onClick={() => setExpanded(!expanded)}
            >
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <User size={18} />
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <strong>{display.name}</strong>
                        <Badge bg={display.isInternal ? 'primary' : 'secondary'}>
                            {display.isInternal ? 'Intern' : 'Extern'}
                        </Badge>
                        {display.role && (
                            <Badge bg="light" text="dark">{display.role}</Badge>
                        )}
                    </div>
                </div>
                {contact.addedBy && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <MetadataInfoButton
                            entries={[
                                {
                                    label: 'Tillagd',
                                    date: contact.addedAt,
                                    userName: addedByName || 'Okänd användare',
                                },
                            ]}
                        />
                    </div>
                )}
                {isAdmin && (
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        disabled={submitting}
                    >
                        <Trash2 size={14} />
                    </Button>
                )}
            </div>

            {expanded && (
                <div style={{
                    padding: '0 16px 16px 44px',
                    borderTop: '1px solid #dee2e6',
                    paddingTop: '12px',
                }}>
                    {display.company && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', color: '#666' }}>
                            <Building size={14} />
                            <span>{display.company}</span>
                        </div>
                    )}
                    {display.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <Mail size={14} style={{ color: '#666' }} />
                            <a href={`mailto:${display.email}`}>{display.email}</a>
                        </div>
                    )}
                    {display.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <Phone size={14} style={{ color: '#666' }} />
                            <a href={`tel:${display.phone}`}>{display.phone}</a>
                        </div>
                    )}

                    {/* Project Note */}
                    <div style={{ marginTop: hasContactDetails ? '12px' : 0, paddingTop: hasContactDetails ? '12px' : 0, borderTop: hasContactDetails ? '1px solid #eee' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '14px', color: '#666' }}>Projektanteckning</strong>
                            {isAdmin && !editingNote && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => setEditingNote(true)}
                                    disabled={submitting}
                                    style={{ padding: 0 }}
                                >
                                    <Edit2 size={12} />
                                </Button>
                            )}
                        </div>
                        {editingNote ? (
                            <div>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={noteValue}
                                    onChange={(e) => setNoteValue(e.target.value)}
                                    placeholder="Lägg till en anteckning..."
                                    autoFocus
                                />
                                <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSaveNote}
                                        disabled={submitting}
                                    >
                                        Spara
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleCancelNote}
                                        disabled={submitting}
                                    >
                                        Avbryt
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <p style={{ margin: 0, color: contact.projectNote ? '#333' : '#999', fontStyle: contact.projectNote ? 'normal' : 'italic' }}>
                                {contact.projectNote || 'Ingen anteckning'}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export const ProjectExternalContactsSection: React.FC<ProjectExternalContactsSectionProps> = ({
    project,
    externalUsers,
    internalUsers,
    getUserName,
    onAddContact,
    onRemoveContact,
    onEditNote,
    onRefetchUsers,
    submitting = false,
    isAdmin,
}) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const { userNames, loadUserNames } = useUserNames();

    // Build map of uid -> user for quick lookup (internal + external registries)
    const externalUserMap = useMemo(() => {
        const map = new Map<string, ExternalUser>();
        externalUsers.forEach(u => map.set(u.uid, u));
        return map;
    }, [externalUsers]);

    const internalUserMap = useMemo(() => {
        const map = new Map<string, Userprofile>();
        internalUsers.forEach(u => map.set(u.key, u));
        return map;
    }, [internalUsers]);

    // Get contacts list from project
    const contacts = useMemo(
        () => project.externalContacts ? Object.entries(project.externalContacts) : [],
        [project.externalContacts]
    );

    // Resolve each contact to a display model. External contacts whose registry
    // entry no longer exists are dropped (orphaned); internal contacts always
    // render, falling back to a resolved name when the profile isn't loaded.
    const resolvedContacts = useMemo(() => {
        return contacts.flatMap(([uid, contact]) => {
            const isInternal = contact.contactType === 'internal';
            if (isInternal) {
                const profile = internalUserMap.get(uid);
                const display: ContactDisplay = {
                    name: profile?.name || getUserName(uid) || profile?.email || 'Okänd användare',
                    email: profile?.email,
                    isInternal: true,
                };
                return [{ uid, contact, display }];
            }
            const externalUser = externalUserMap.get(uid);
            if (!externalUser) {
                console.warn(`External user not found for contact: ${uid}`);
                return [];
            }
            const display: ContactDisplay = {
                name: externalUser.name,
                company: externalUser.company,
                email: externalUser.email,
                phone: externalUser.phone,
                role: externalUser.role,
                isInternal: false,
            };
            return [{ uid, contact, display }];
        });
    }, [contacts, internalUserMap, externalUserMap, getUserName]);

    // Get IDs of already linked contacts (for disabling in selector)
    const linkedContactIds = useMemo(
        () => contacts.map(([uid]) => uid),
        [contacts]
    );

    // Extract UIDs for user name loading (who added each contact)
    const contactUids = useMemo(
        () => contacts.map(([, contact]) => contact.addedBy).filter(Boolean) as string[],
        [contacts]
    );

    // Load user names for all contacts' addedBy
    useEffect(() => {
        if (contactUids.length > 0) {
            loadUserNames(contactUids);
        }
    }, [contactUids, loadUserNames]);

    const handleRemoveClick = (externalUserId: string) => {
        setPendingDeleteId(externalUserId);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (pendingDeleteId) {
            await onRemoveContact(pendingDeleteId);
            setPendingDeleteId(null);
        }
    };

    const pendingContact = pendingDeleteId
        ? resolvedContacts.find(c => c.uid === pendingDeleteId)
        : null;

    return (
        <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #dee2e6'
        }}>
            <div style={{ border: '1px solid #dee2e6', borderRadius: '10px', backgroundColor: '#fff' }}>
                <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px 16px',
                    borderBottom: '1px solid #dee2e6',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h5 style={{ margin: 0 }}>Kontaktlista</h5>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="kontaktlista-info">{KONTAKTLISTA_INFO}</Tooltip>}
                            >
                                <span style={{ display: 'inline-flex', color: '#6c757d', cursor: 'help' }}>
                                    <Info size={16} />
                                </span>
                            </OverlayTrigger>
                            {resolvedContacts.length > 0 && (
                                <Badge bg="secondary">{resolvedContacts.length}</Badge>
                            )}
                        </div>
                        {isAdmin && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setShowAddModal(true)}
                                disabled={submitting}
                            >
                                <Plus size={14} className="me-1" />
                                Lägg till kontakt
                            </Button>
                        )}
                    </div>
                </div>
                <div style={{ padding: '24px' }}>
                    {resolvedContacts.length > 0 ? (
                        <div>
                            {resolvedContacts.map(({ uid, contact, display }) => (
                                <ContactItem
                                    key={uid}
                                    contact={contact}
                                    display={display}
                                    onRemove={() => handleRemoveClick(uid)}
                                    onEditNote={(note) => onEditNote(uid, note)}
                                    submitting={submitting}
                                    addedByName={contact.addedBy ? userNames[contact.addedBy] || undefined : undefined}
                                    isAdmin={isAdmin}
                                />
                            ))}
                        </div>
                    ) : (
                        <p style={{ color: '#666', fontStyle: 'italic', margin: 0 }}>
                            Inga kontakter har lagts till.
                        </p>
                    )}
                </div>
            </div>

            <AddExternalContactModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onAdd={onAddContact}
                onRefetchUsers={onRefetchUsers}
                submitting={submitting}
                linkedContactIds={linkedContactIds}
                internalUsers={internalUsers}
            />

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setPendingDeleteId(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Ta bort kontakt"
                message={pendingContact
                    ? `Vill du ta bort ${pendingContact.display.name} från projektets kontaktlista?${pendingContact.display.isInternal ? '' : ' Kontakten finns kvar i det centrala registret.'}`
                    : 'Vill du ta bort denna kontakt från projektet?'
                }
            />
        </div>
    );
};
